import {
  UpsellCompareAtPriceDisplay,
  UpsellProductRulesActionExpressionPayload,
  UpsellProductRulesDto,
} from "@intelligems/ig-types";
import { postfixToExpressionList } from "@intelligems/ig-utils";
import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "@src/store/rootState";
import { getUpsellProductRules } from "@src/store/slices/upsellProductRules/index";

export const getCartTargetingForRule =
  (ruleId: string) =>
  (state: RootState): string | undefined => {
    const rules = getUpsellProductRules(state);
    const rule = rules.find((r) => r.id === ruleId);
    return rule?.cartTargeting?.id;
  };

export const getUpsellProductRulesByCheckoutBlockId = (
  checkoutBlockId: string
) =>
  createSelector(getUpsellProductRules, (rules): UpsellProductRulesDto[] =>
    rules.filter((rule) => rule.checkoutBlockId === checkoutBlockId)
  );

export const getUpsellProductRulesByVariationId = (variationId: string) =>
  createSelector(getUpsellProductRules, (rules): UpsellProductRulesDto[] =>
    rules.filter((rule) => rule.variationId === variationId)
  );

export const getUpsellProductRulesByCheckoutBlockIds = (
  checkoutBlockIds: string[]
) =>
  createSelector(getUpsellProductRules, (rules): UpsellProductRulesDto[] => {
    const checkoutBlockIdSet = new Set(checkoutBlockIds);
    return rules.filter((rule) => checkoutBlockIdSet.has(rule.checkoutBlockId));
  });

export const getUpsellProductRuleActionExpressionList = (
  ruleId: string
): ((state: RootState) => UpsellProductRulesActionExpressionPayload[]) =>
  createSelector(
    [
      (state: RootState) => {
        const rules = getUpsellProductRules(state);
        const rule = rules.find((r) => r.id === ruleId);
        return rule?.action.expression;
      },
    ],
    (expression): UpsellProductRulesActionExpressionPayload[] => {
      if (expression && expression.length > 0) {
        return postfixToExpressionList(
          expression,
          "upsellProductTargeting"
        ) as UpsellProductRulesActionExpressionPayload[];
      }
      return [];
    }
  );

export const getSyncedDiscountForCheckoutBlock = (checkoutBlockId: string) =>
  createSelector(
    getUpsellProductRules,
    (
      upsellProductRules
    ): {
      discountType: "none" | "percentage" | "fixed";
      discountValue: number | null;
      compareAtPriceDisplay: UpsellCompareAtPriceDisplay;
    } => {
      const blockRules = upsellProductRules.filter(
        (rule) => rule.checkoutBlockId === checkoutBlockId
      );

      if (blockRules.length === 0) {
        return {
          discountType: "none",
          discountValue: null,
          compareAtPriceDisplay: "none",
        };
      }

      const firstRule = blockRules[0];
      return {
        discountType: firstRule.action.discountType,
        discountValue: firstRule.action.discountValue ?? null,
        compareAtPriceDisplay: firstRule.action.compareAtPriceDisplay,
      };
    }
  );

/**
 * Helper function to strip displayData from a product ID value item.
 */
const stripDisplayDataFromItem = (item: any): any => {
  if (typeof item === "object" && item !== null) {
    const { displayData: _, ...cleanItem } = item;
    return cleanItem;
  }
  return item;
};

const stripDisplayDataFromExpression = (expr: any): any => {
  if (!expr.query) {
    return expr;
  }

  const { displayData, ...queryWithoutDisplayData } = expr.query;

  const cleanQuery = Object.fromEntries(
    Object.entries(queryWithoutDisplayData).filter(([_, v]) => v !== undefined)
  );

  if (cleanQuery.type === "productId" && Array.isArray(cleanQuery.value)) {
    return {
      ...expr,
      query: {
        ...cleanQuery,
        value: cleanQuery.value.map(stripDisplayDataFromItem),
      },
    };
  }

  return {
    ...expr,
    query: cleanQuery,
  };
};

const stripDisplayDataFromRules = (
  rules: UpsellProductRulesDto[]
): UpsellProductRulesDto[] => {
  return rules.map((rule) => ({
    ...rule,
    action: {
      ...rule.action,
      expression: rule.action.expression.map(stripDisplayDataFromExpression),
    },
  })) as UpsellProductRulesDto[];
};

export const getUpsellProductRulesForPayload = createSelector(
  getUpsellProductRules,
  stripDisplayDataFromRules
);

export const getStoredUpsellProductRulesForPayload = createSelector(
  (state: RootState) => state.upsellProductRules.storedUpsellProductRules,
  stripDisplayDataFromRules
);
